import type { GlobalContext, Surface } from './types';

/**
 * Supplies the context every event is stamped with, without the telemetry
 * module importing a single store.
 *
 * Stores report, so a store import here would close a cycle; the app registers
 * a provider on startup instead. Values are pulled at report time rather than
 * captured once, because the tenant is settled before the SDK loads but the
 * native app version only arrives with the React Native handshake, which can
 * land after the first report.
 */
let provider: (() => GlobalContext) | null = null;

export const setGlobalContextProvider = (next: () => GlobalContext): void => {
  provider = next;
};

/**
 * The surface the session runs on.
 *
 * Exported because analytics tags its events with the same value: GA and the
 * error logs are read side by side, and a second derivation of "which surface
 * is this" would drift from this one the first time either changed. Notably,
 * the platform comes from the React Native handshake rather than the user
 * agent, so `rn-unknown` means the handshake has not landed yet — a state the
 * user agent cannot express.
 *
 * The fallback lives here rather than in each caller for the same reason: two
 * callers guessing separately is the drift this export exists to prevent. It is
 * reached only before the app registers a provider, when all that is knowable
 * is whether we are inside the shell.
 */
export const currentSurface = (): Surface => {
  if (provider) {
    try {
      return provider().surface;
    } catch {
      // fall through to the coarse guess below
    }
  }
  if (import.meta.env.SSR) {
    return 'ssr';
  }
  return window.ReactNativeWebView ? 'rn-unknown' : 'web';
};

/**
 * The tags shared by every event.
 *
 * `company` is the one this whole taxonomy turns on: without it a tenant is
 * identifiable only by guessing at the URL in `culprit`, which is empty for
 * anything reported off a route. A provider that throws must not take the
 * report down with it — a report with no tenant still beats no report.
 */
export const globalTags = (): Record<string, string> => {
  if (!provider) {
    return {};
  }
  try {
    const { company, surface, appVersion, appBuild, deviceId } = provider();
    const tags: Record<string, string> = { company, surface };
    if (appVersion) {
      tags.app_version = appVersion;
    }
    if (appBuild) {
      tags.app_build = appBuild;
    }
    if (deviceId) {
      tags.device_id = deviceId;
    }
    return tags;
  } catch {
    return {};
  }
};

export const currentDeviceId = (): string | null => {
  if (!provider) {
    return null;
  }
  try {
    return provider().deviceId ?? null;
  } catch {
    return null;
  }
};
